import { useEffect, useMemo, useRef } from 'react';

import { Alert, Box, Card, Heading, Icon, Paragraph, Text, TextButton, Upsell } from '@zillow/constellation-10';
import {
  IconHandshakeFilled,
  IconMailFilled,
  IconPiggyBankFilled,
  IconYardSignFilled,
} from '@zillow/constellation-icons';

import { logError } from '@zg-rentals/log-error';
import { useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useScreenSize } from '../../../../../hooks';
import { useGetListingDetails } from '../../../../../hooks/queries/properties';
import accessUtils from '../../../../../utils/accessUtils';

import { ErrorMessageConstants } from '../../../../../constants/ErrorMessageConstants';

import rentHome from '../../../../../assets/rent-home.svg';
import { actionCardStyles, sectionFlexStyles } from './styles';

import {
  emitAddLeaseEvent,
  emitCreateListingActionEvent,
  emitInviteToApplyEvent,
  emitSetUpPaymentsEvent,
  EVENT_IDS,
} from '../../../../../analytics/events/selectAvailableActions';
import { useAuth } from '../../../../AuthProvider';

import type { RentalManagerAuthCheckUserInfoWebApiDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { SelectAvailableActionsTriggerObject } from '../../../../../analytics/events/selectAvailableActions';

type Props = {
  listingAlias: string;
  triggerObject?: SelectAvailableActionsTriggerObject;
  user?: Partial<RentalManagerAuthCheckUserInfoWebApiDto>;
};

const SelectAvailableActions = ({ listingAlias, triggerObject = 'posting_path', user: userProp }: Props) => {
  const { data: listingDetailsData } = useGetListingDetails(listingAlias, { enabled: Boolean(listingAlias) });
  const isStreetEasyRestricted =
    accessUtils.canAddStreetEasyProperty() &&
    Boolean(listingDetailsData?.restrictions?.isStreetEasyLocationCreateListingRestricted);

  const { isScreenLgGTE } = useScreenSize();

  const authContext = useAuth?.();
  const user = userProp ?? authContext?.user;

  const { data: webAnalyticsData } = useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get(
    {
      listingAlias,
      includeLeases: false,
    },
    { enabled: Boolean(listingAlias) },
  );

  const csEventParams = useMemo(() => {
    if (!webAnalyticsData || Object.keys(webAnalyticsData).length === 0) {
      logError({
        error: 'webAnalyticsData is empty:',
        errorType: 'SelectAvailableActions',
        context: `SelectAvailableActions@csEventParams. listingAlias: ${listingAlias}`,
      });
    }

    return { user: user ?? {}, currentProperty: webAnalyticsData ?? {}, triggerObject };
  }, [webAnalyticsData, user, listingAlias, triggerObject]);

  const topMostElementRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    topMostElementRef?.current?.scrollIntoView(false);
  }, []);

  const handleLinkCreateListingClick = () => {
    if (user && webAnalyticsData) {
      emitCreateListingActionEvent(csEventParams);
    }
  };

  const handleLinkApplicationClick = () => {
    if (user && webAnalyticsData) {
      emitInviteToApplyEvent(csEventParams);
    }
  };

  const handleLinkLeaseClick = () => {
    if (user && webAnalyticsData) {
      emitAddLeaseEvent(csEventParams);
    }
  };

  const handleLinkPaymentClick = () => {
    if (user && webAnalyticsData) {
      emitSetUpPaymentsEvent(csEventParams);
    }
  };

  return (
    <>
      {/* oxlint-disable-next-line next/no-img-element -- src is normalized to a plain string (not the StaticImageData object), so next/image can't auto-infer dimensions, and no explicit width/height is set here */}
      <img
        ref={topMostElementRef}
        src={typeof rentHome === 'string' ? rentHome : rentHome.src}
        alt={'rent home'}
        style={{
          display: 'block',
          marginInline: 'auto',
          paddingBottom: '400',
          width: isScreenLgGTE ? '60%' : undefined,
        }}
      />

      <Heading level={4} css={{ textAlign: 'center', paddingBottom: { lg: '100' } }}>
        You added your property
      </Heading>
      <Paragraph css={{ textAlign: 'center', marginBottom: '800' }}>
        You’re one step closer to listing on our networks
        <br />
        with over 1 million daily visitors across Zillow, Trulia, and HotPads.
      </Paragraph>
      <Upsell
        action={
          isStreetEasyRestricted ? (
            <TextButton asChild>
              <a href={`/rental-manager/properties/${listingAlias}/dashboard/overview`}>Go to dashboard</a>
            </TextButton>
          ) : (
            <TextButton asChild onClick={handleLinkCreateListingClick}>
              <a href={`/rental-manager/properties/${listingAlias}/listing`}>Create listing</a>
            </TextButton>
          )
        }
        body={
          isStreetEasyRestricted ? (
            <Alert data-testid="select-action-streeteasy-restriction">
              {ErrorMessageConstants.LOCATION_RESTRICTION.NYC}
            </Alert>
          ) : (
            <Paragraph>It’s free to post and takes just a few minutes!</Paragraph>
          )
        }
        header={
          <Heading level={4}>
            {isStreetEasyRestricted ? 'Manage your property' : 'You’re one step closer to listing on our networks'}
          </Heading>
        }
        media={<Icon render={<Icon render={<IconYardSignFilled />} />} />}
        tone="impact"
        elevated={false}
        closeable={false}
        css={{
          // Padding is for blue section
          paddingBlock: { base: '600', lg: '800' },
          paddingInline: '400',
          // Margins is to make gaps around the blue section
          marginInline: { lg: '1800', xl: '2400' },
          marginBottom: { base: '400', lg: '600' },
        }}
      />
      <Box
        css={{
          display: 'flex',
          flexDirection: { base: 'column', lg: 'row' },
          alignItems: 'stretch',
          justifyContent: 'space-between',
          gap: { base: '400', lg: '600' },
          marginInline: { lg: '1800', xl: '2400' },
          marginBottom: { lg: '1200' },
        }}
      >
        <Card elevated={false} css={actionCardStyles}>
          <Box css={sectionFlexStyles}>
            <Icon size={{ base: 'xl', md: 'lg' }} render={<IconMailFilled />} />
            <div>
              <Text asChild css={{ marginBlock: { lg: '200' }, marginRight: { base: '800', lg: '600', xl: '400' } }}>
                <p>Screen renters with our hassle-free applications</p>
              </Text>
              <TextButton
                asChild
                css={{ marginTop: '200', fontSize: { base: 'body', lg: 'body-sm' } }}
                onClick={handleLinkApplicationClick}
                data-eventid={EVENT_IDS.INVITE_TO_APPLY}
              >
                <a href={`/rental-manager/applications/property/${listingAlias}`}>Invite someone to apply</a>
              </TextButton>
            </div>
          </Box>
        </Card>
        <Card elevated={false} css={actionCardStyles}>
          <Box css={sectionFlexStyles}>
            <Icon size={{ base: 'xl', md: 'lg' }} render={<IconHandshakeFilled />} />
            <div>
              <Text asChild css={{ marginBlock: { lg: '200' }, marginRight: { base: '800', lg: '600', xl: '400' } }}>
                <p>Upload a lease for signing</p>
              </Text>
              <TextButton
                asChild
                css={{ marginTop: '200', fontSize: { base: 'body', lg: 'body-sm' } }}
                onClick={handleLinkLeaseClick}
                data-eventid={EVENT_IDS.ADD_LEASE}
              >
                <a href={`/rental-manager/leasing/property/${listingAlias}`}>Add a lease </a>
              </TextButton>
            </div>
          </Box>
        </Card>
        <Card elevated={false} css={actionCardStyles}>
          <Box css={sectionFlexStyles}>
            <Icon size={{ base: 'xl', md: 'lg' }} render={<IconPiggyBankFilled />} />
            <div>
              <Text asChild css={{ marginBlock: { lg: '200' }, marginRight: { base: '800', lg: '600', xl: '400' } }}>
                <p>Collect rent payments at no cost to you</p>
              </Text>
              <TextButton
                asChild
                css={{ marginTop: '200', fontSize: { base: 'body', lg: 'body-sm' } }}
                onClick={handleLinkPaymentClick}
                data-eventid={EVENT_IDS.SET_UP_PAYMENTS}
              >
                <a href={`/rental-manager/payments/properties/${listingAlias}`}>Set up payments</a>
              </TextButton>
            </div>
          </Box>
        </Card>
      </Box>
    </>
  );
};

export default SelectAvailableActions;
